import { sortWardNames } from './wards';

/** Values an organizer has used on earlier events, most recent first. */
export interface UnitHistoryItem { stakeName: string; wardName: string | null; level: 'stake' | 'ward'; wardNames?: string[]; leaderName?: string; leaderPhone?: string }

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');

function uniqueByNorm(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const t = v.trim();
    if (!t || seen.has(norm(t))) continue;
    seen.add(norm(t));
    out.push(t);
  }
  return out;
}

/** `history` must be ordered most recent first. */
export function stakeSuggestions(history: UnitHistoryItem[]): string[] {
  return uniqueByNorm(history.map((h) => h.stakeName));
}

/** Every ward name an event mentions: its own ward, or its stake-event ward list. */
function wardsOf(h: UnitHistoryItem): string[] {
  return h.level === 'ward' ? (h.wardName ? [h.wardName] : []) : h.wardNames ?? [];
}

/** Wards used under the given stake come first, then every other ward the organizer has used. */
export function wardSuggestions(history: UnitHistoryItem[], stakeName: string): string[] {
  const sameStake = history.filter((h) => norm(h.stakeName) === norm(stakeName)).flatMap(wardsOf);
  return uniqueByNorm([...sameStake, ...history.flatMap(wardsOf)]);
}

/** The ward list from the most recent stake event under this stake, or [] if there is none. */
export function rememberedWardNames(history: UnitHistoryItem[], stakeName: string): string[] {
  if (!stakeName.trim()) return [];
  const hit = history.find((h) => h.level === 'stake' && norm(h.stakeName) === norm(stakeName) && h.wardNames?.length);
  return hit ? sortWardNames(hit.wardNames) : [];
}

/** The ward of the most recent ward-level event under this stake, or '' if there is none. Stake ward lists are ignored. */
export function lastWardName(history: UnitHistoryItem[], stakeName: string): string {
  if (!stakeName.trim()) return '';
  const hit = history.find((h) => h.level === 'ward' && h.wardName?.trim() && norm(h.stakeName) === norm(stakeName));
  return hit ? hit.wardName!.trim() : '';
}

/** Defaults for a new event: the most recent stake, and the ward of the most recent ward event under it. */
export function defaultUnit(history: UnitHistoryItem[]): { stakeName: string; wardName: string } {
  const stakeName = stakeSuggestions(history)[0] ?? '';
  return { stakeName, wardName: lastWardName(history, stakeName) };
}

/** Past leader names and phones, most recent first. */
export function leaderNameSuggestions(history: UnitHistoryItem[]): string[] {
  return uniqueByNorm(history.map((h) => h.leaderName ?? ''));
}

export function leaderPhoneSuggestions(history: UnitHistoryItem[]): string[] {
  return uniqueByNorm(history.map((h) => h.leaderPhone ?? ''));
}

/** The most recent phone used with this leader name, or null if the name is new. */
export function phoneForLeader(history: UnitHistoryItem[], name: string): string | null {
  const hit = history.find((h) => h.leaderName && h.leaderPhone && norm(h.leaderName) === norm(name));
  return hit ? hit.leaderPhone!.trim() : null;
}

/** Leader defaults for a new event: the name and phone from the most recent event. */
export function defaultLeader(history: UnitHistoryItem[]): { leaderName: string; leaderPhone: string } {
  const h = history.find((x) => x.leaderName?.trim());
  return { leaderName: h?.leaderName?.trim() ?? '', leaderPhone: h?.leaderPhone?.trim() ?? '' };
}
